import type { KeyboardEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { IconList, RadioSwitchTheme } from '@snowit/icetypes';
import type { A11yEvent } from '@snowit/icetypes/events';

import useA11y from '@snowit/hooks/useA11y';
import { makeCSS } from '@snowit/utils/misc';
import testId from '@snowit/utils/testId';

import Icon from '../../Icon';
import Typo from '../../Typography/Typo';

import cx from 'classnames';
import s from './RadioSwitch.module.scss';

export type RadioOption<T extends any> = {
  key: string;
  id?: string;
  icon?: IconList;
  label?: string;
  value: T;
  checked?: boolean;
  disabled?: boolean;
  test?: string;
};

export type IRadioSwitch<T extends any> = {
  theme?: RadioSwitchTheme;
  options: RadioOption<T>[];
  name?: string;
  value?: T;
  onChange?: (params: { value: T; event?: A11yEvent<HTMLInputElement> }) => void;
  disabled?: boolean;
  className?: string;
  test?: string;
};

const RadioSwitch = <T extends any>(props: IRadioSwitch<T>) => {
  const { theme = 'default', options, value, onChange, disabled, className, test } = props;
  const a11yH = useA11y();

  const [activeIndex, setActiveIndex] = useState(-1);

  const radioRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const navigableKeys = useMemo(() => {
    return options.filter((option) => !option.disabled && !disabled).map((entry) => entry.key);
  }, [options]);

  useEffect(() => {
    const index = navigableKeys[activeIndex];
    if (radioRefs.current[index]) {
      radioRefs.current[index].focus();
    }
  }, [activeIndex, JSON.stringify(navigableKeys)]);

  const handleChange = (option: RadioOption<T>, event: A11yEvent<HTMLInputElement>) => {
    if (disabled || option.disabled) return;
    if (typeof onChange === 'function') {
      onChange({
        value: option.value,
        event,
      });
    }
  };

  const handleKeyDown = (option: RadioOption<T>, event: A11yEvent<HTMLInputElement>) => {
    a11yH.handleListNav({
      event: event as KeyboardEvent<HTMLInputElement>,
      active: activeIndex,
      total: navigableKeys.length,
      setIndex: setActiveIndex,
      onEnter: () => handleChange(option, event),
      onEsc: () => {},
      blockDefault: () => true,
    });
  };

  const renderOption = (option: RadioOption<T>, index: number) => {
    const checked = option.checked || value === option.value;

    return (
      <div
        className={cx(s.input, {
          [s.checked]: checked,
          [s.disabled]: option.disabled || disabled,
        })}
        key={option.key}
      >
        <input
          ref={(el) => {
            radioRefs.current[option.key] = el;
          }}
          className={s.radio}
          type='radio'
          id={option.key || `key_${index}`}
          value={option.value ? String(option.value) : ''}
          name={'switch'}
          checked={checked}
          onChange={(event) => handleChange(option, event)}
          disabled={option.disabled || disabled}
          onKeyDown={(e) => handleKeyDown(option, e)}
          onFocus={() => setActiveIndex(index)}
          aria-label={option.label ?? option.key}
          {...testId(option.key)}
        />
        {option.icon && <Icon className={s.icon} name={option.icon} />}
        {option.label && <Typo className={s.label} text={option.label} tag={'label'} />}
      </div>
    );
  };

  return (
    <div
      className={cx(s.switch, className, {
        [s[theme]]: true,
        [s.disabled]: disabled,
      })}
      style={makeCSS({ '--l-item-number': options.length })}
      role={'radiogroup'}
      {...testId(test)}
    >
      {options.map((option, i) => renderOption(option, i))}
    </div>
  );
};

export default RadioSwitch;
